import { useMemo, useState } from "react";
import { ActoDrawer } from "@/components/ActoDrawer";
import { Card, CardHeader } from "@/components/Card";
import {
  categorize,
  formatFecha,
  getYear,
  sortMovimientos,
  toneFor,
  type ActoCategory,
  type ActoTone,
} from "@/lib/folio-analysis";
import type { Movimiento } from "@/lib/types";

const ICON_DOT_CLASS: Record<ActoTone, string> = {
  rose: "bg-rose-500 ring-rose-100",
  emerald: "bg-emerald-500 ring-emerald-100",
  amber: "bg-amber-500 ring-amber-100",
  indigo: "bg-indigo-500 ring-indigo-100",
  slate: "bg-slate-400 ring-slate-100",
};

function CategoryIcon({ category }: { category: ActoCategory }) {
  const common = {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: "h-3.5 w-3.5 text-white",
    "aria-hidden": true,
  };
  switch (category) {
    case "gravamen":
      return (
        <svg {...common}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
      );
    case "cancelacion":
      return (
        <svg {...common}>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      );
    case "aviso":
      return (
        <svg {...common}>
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
      );
    case "compraventa":
      return (
        <svg {...common}>
          <path d="M8 3 4 7l4 4" />
          <path d="M4 7h16" />
          <path d="m16 21 4-4-4-4" />
          <path d="M20 17H4" />
        </svg>
      );
    case "aclaratoria":
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="m18 14-3.59 3.59a1 1 0 0 1-.71.29H12v-2.71a1 1 0 0 1 .29-.71L16 11" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
      );
  }
}

const ALL = "__all__";

export function MovimientosTimeline({
  folioId,
  movimientos,
}: {
  folioId: string;
  movimientos: Movimiento[];
}) {
  const [tipo, setTipo] = useState<string>(ALL);
  const [year, setYear] = useState<string>(ALL);
  const [selected, setSelected] = useState<Movimiento | null>(null);

  const availableTipos = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of movimientos) {
      if (!map.has(m.clave)) map.set(m.clave, m.acto);
    }
    return Array.from(map.entries())
      .map(([clave, acto]) => ({ clave, label: acto }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [movimientos]);

  const availableYears = useMemo(() => {
    const set = new Set<string>();
    for (const m of movimientos) set.add(getYear(m.fechaRegistro));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [movimientos]);

  const groups = useMemo(() => {
    const filtered = movimientos.filter((m) => {
      if (tipo !== ALL && m.clave !== tipo) return false;
      if (year !== ALL && getYear(m.fechaRegistro) !== year) return false;
      return true;
    });
    const sorted = sortMovimientos(filtered);
    const byYear = new Map<string, typeof sorted>();
    for (const m of sorted) {
      const y = getYear(m.fechaRegistro);
      const arr = byYear.get(y) ?? [];
      arr.push(m);
      byYear.set(y, arr);
    }
    return Array.from(byYear.entries());
  }, [movimientos, tipo, year]);

  const filtersActive = tipo !== ALL || year !== ALL;
  const visibleCount = groups.reduce((sum, [, items]) => sum + items.length, 0);

  return (
    <Card>
      <CardHeader
        title={`Movimientos (${movimientos.length})`}
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        }
        action={
          filtersActive ? (
            <button
              type="button"
              onClick={() => {
                setTipo(ALL);
                setYear(ALL);
              }}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              Limpiar filtros
            </button>
          ) : null
        }
      />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Tipo"
          value={tipo}
          onChange={setTipo}
          options={[
            { value: ALL, label: "Todos los tipos" },
            ...availableTipos.map((t) => ({ value: t.clave, label: t.label })),
          ]}
        />
        <FilterSelect
          label="Año"
          value={year}
          onChange={setYear}
          options={[
            { value: ALL, label: "Todos los años" },
            ...availableYears.map((y) => ({ value: y, label: y })),
          ]}
        />
      </div>
      {filtersActive && (
        <p className="mt-2 text-xs text-slate-500">
          Mostrando {visibleCount} de {movimientos.length} movimientos
        </p>
      )}

      {groups.length === 0 ? (
        <p className="mt-6 text-center text-sm text-slate-500">
          No hay movimientos que coincidan con el filtro.
        </p>
      ) : (
        <div className="mt-6 space-y-8">
          {groups.map(([year, items]) => (
            <div key={year}>
              <div className="mb-4 flex items-center gap-3">
                <span className="font-mono text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {year}
                </span>
                <span className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400">
                  {items.length} {items.length === 1 ? "evento" : "eventos"}
                </span>
              </div>

              <ol className="relative space-y-1 border-l border-slate-200 pl-6">
                {items.map((m, i) => {
                  const cat = categorize(m);
                  const tone = toneFor(cat);
                  return (
                    <li
                      key={`${m.clave}-${m.control}-${m.consecutivo}-${i}`}
                      className="relative"
                    >
                      <span
                        className={`absolute -left-[34px] top-3.5 grid h-6 w-6 place-items-center rounded-full ring-4 ${ICON_DOT_CLASS[tone]}`}
                      >
                        <CategoryIcon category={cat} />
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelected(m)}
                        className="group flex w-full flex-wrap items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-slate-50"
                      >
                        <span className="font-medium text-slate-900">
                          {m.acto}
                        </span>
                        <span className="text-xs text-slate-500">
                          {formatFecha(m.fechaRegistro)}
                        </span>
                        <span className="ml-auto flex items-center gap-3 text-xs text-slate-500">
                          <span>
                            Control{" "}
                            <span className="font-mono text-slate-700">
                              {m.control}/{m.consecutivo}
                            </span>
                          </span>
                          {m.escrituraOficio &&
                            m.escrituraOficio !== "." && (
                              <span>
                                Esc.{" "}
                                <span className="font-mono text-slate-700">
                                  {m.escrituraOficio}
                                </span>
                              </span>
                            )}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-slate-500"
                            aria-hidden
                          >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                          </svg>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <ActoDrawer
          folioId={folioId}
          movimiento={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </Card>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-500">
      <span className="font-medium uppercase tracking-wider">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-lg border border-slate-200 bg-white py-1.5 pl-3 pr-8 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
